import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Users, Shield, Search, Loader2, Crown, User, ChevronLeft, ChevronRight, Video, Wallet, Eye, Plus, Camera, FolderOpen, Palette } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: "free", label: "Free", color: "bg-slate-100 text-slate-600" },
  { value: "beginner", label: "Beginner", color: "bg-gray-100 text-gray-700" },
  { value: "pro", label: "Pro", color: "bg-amber-100 text-amber-700" },
  { value: "admin", label: "Admin", color: "bg-purple-100 text-purple-700" },
];

const getRoleBadge = (role) => {
  const roleConfig = ROLES.find(r => r.value === role) || ROLES[0];
  return (
    <span className={`text-xs px-2 py-1 rounded-full font-medium ${roleConfig.color}`}>
      {roleConfig.label}
    </span>
  );
};

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState("");
  const [showRoleDialog, setShowRoleDialog] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [pagination, setPagination] = useState({ skip: 0, limit: 20, total: 0 });
  const [profileUser, setProfileUser] = useState(null);
  const [showProfileDialog, setShowProfileDialog] = useState(false);
  const [profileLoading, setProfileLoading] = useState(false);
  const [showAddCreditsDialog, setShowAddCreditsDialog] = useState(false);
  const [creditAmount, setCreditAmount] = useState("");
  const [addingCredits, setAddingCredits] = useState(false);

  useEffect(() => {
    if (!isAdmin) {
      toast.error("Admin access required");
      navigate("/dashboard");
      return;
    }
    fetchUsers();
  }, [isAdmin, navigate, pagination.skip]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/auth/users`, {
        params: { skip: pagination.skip, limit: pagination.limit },
        withCredentials: true,
      });
      setUsers(response.data.users);
      setPagination(prev => ({ ...prev, total: response.data.total }));
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = (user) => {
    setSelectedUser(user);
    setNewRole(user.role || "beginner");
    setShowRoleDialog(true);
  };

  const updateUserRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      setUpdating(true);
      await axios.post(
        `${API}/api/auth/users/update-role`,
        { user_id: selectedUser.user_id, role: newRole },
        { withCredentials: true }
      );
      toast.success(`User role updated to ${newRole}`);
      setShowRoleDialog(false);
      fetchUsers();
    } catch (error) {
      console.error("Error updating role:", error);
      toast.error(error.response?.data?.detail || "Failed to update role");
    } finally {
      setUpdating(false);
    }
  };

  const viewProfile = async (u) => {
    setProfileLoading(true);
    setShowProfileDialog(true);
    try {
      const res = await axios.get(`${API}/api/auth/users/${u.user_id}`, { withCredentials: true });
      setProfileUser(res.data);
    } catch (error) {
      toast.error("Failed to load user profile");
      setShowProfileDialog(false);
    } finally {
      setProfileLoading(false);
    }
  };

  const addCredits = async () => {
    const amount = parseFloat(creditAmount);
    if (!amount || amount <= 0) return toast.error("Enter a valid credit amount");
    try {
      setAddingCredits(true);
      const res = await axios.post(`${API}/api/auth/users/add-credits`, {
        user_id: profileUser.user_id,
        amount,
      }, { withCredentials: true });
      toast.success(`Added ${amount} credits. New balance: ${res.data.new_balance}`);
      setShowAddCreditsDialog(false);
      setCreditAmount("");
      // Refresh profile
      viewProfile(profileUser);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add credits");
    } finally {
      setAddingCredits(false);
    }
  };

  const filteredUsers = users.filter(
    (u) =>
      u.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      u.name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const totalPages = Math.ceil(pagination.total / pagination.limit);
  const currentPage = Math.floor(pagination.skip / pagination.limit) + 1;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate("/dashboard")}
                data-testid="back-to-dashboard"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
              <div className="h-6 w-px bg-border" />
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-purple-600" />
                <h1 className="text-xl font-semibold">Admin Panel</h1>
              </div>
            </div>
            <Button onClick={() => navigate('/admin/templates')} variant="outline">
              <Video className="w-4 h-4 mr-2" />
              Manage Templates
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Users className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">{pagination.total}</p>
                <p className="text-sm text-muted-foreground">Total Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Crown className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {users.filter((u) => u.role === "pro").length}
                </p>
                <p className="text-sm text-muted-foreground">Pro Users</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-border p-6">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Shield className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-semibold">
                  {users.filter((u) => u.role === "admin").length}
                </p>
                <p className="text-sm text-muted-foreground">Admins</p>
              </div>
            </div>
          </div>
        </div>

        {/* Users Table */}
        <div className="bg-white rounded-xl border border-border">
          <div className="p-4 border-b border-border">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-users-input"
                />
              </div>
              <Button variant="outline" onClick={fetchUsers} disabled={loading}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Refresh"}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Provider</TableHead>
                    <TableHead>Verified</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img
                              src={u.picture}
                              alt={u.name}
                              className="w-8 h-8 rounded-full"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                              <User className="w-4 h-4 text-muted-foreground" />
                            </div>
                          )}
                          <span className="font-medium">{u.name || "—"}</span>
                        </div>
                      </TableCell>
                      <TableCell className="text-muted-foreground">{u.email}</TableCell>
                      <TableCell>{getRoleBadge(u.role)}</TableCell>
                      <TableCell>
                        <span className="text-xs px-2 py-1 rounded bg-muted">
                          {u.auth_provider || "email"}
                        </span>
                      </TableCell>
                      <TableCell>
                        {u.email_verified ? (
                          <span className="text-green-600 text-sm">✓ Verified</span>
                        ) : (
                          <span className="text-amber-600 text-sm">Pending</span>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {u.created_at
                          ? new Date(u.created_at).toLocaleDateString()
                          : "—"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => viewProfile(u)}
                            data-testid={`view-profile-btn-${u.user_id}`}
                          >
                            <Eye className="w-3.5 h-3.5 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRoleChange(u)}
                            disabled={u.user_id === user?.user_id}
                            data-testid={`change-role-btn-${u.user_id}`}
                          >
                            Change Role
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {/* Pagination */}
              <div className="flex items-center justify-between p-4 border-t border-border">
                <p className="text-sm text-muted-foreground">
                  Showing {pagination.skip + 1} to{" "}
                  {Math.min(pagination.skip + pagination.limit, pagination.total)} of{" "}
                  {pagination.total} users
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        skip: Math.max(0, prev.skip - prev.limit),
                      }))
                    }
                    disabled={pagination.skip === 0}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm">
                    Page {currentPage} of {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        skip: prev.skip + prev.limit,
                      }))
                    }
                    disabled={pagination.skip + pagination.limit >= pagination.total}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </div>
      </main>

      {/* Change Role Dialog */}
      <Dialog open={showRoleDialog} onOpenChange={setShowRoleDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Change User Role</DialogTitle>
            <DialogDescription>
              Update the role for {selectedUser?.name || selectedUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">Select Role</label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger data-testid="role-select">
                <SelectValue placeholder="Select a role" />
              </SelectTrigger>
              <SelectContent>
                {ROLES.map((role) => (
                  <SelectItem key={role.value} value={role.value}>
                    <div className="flex items-center gap-2">
                      <span
                        className={`w-2 h-2 rounded-full ${
                          role.value === "admin"
                            ? "bg-purple-500"
                            : role.value === "pro"
                            ? "bg-amber-500"
                            : role.value === "beginner"
                            ? "bg-gray-400"
                            : "bg-slate-400"
                        }`}
                      />
                      {role.label}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-2">
              {newRole === "admin" && "Full access + can manage users and templates"}
              {newRole === "pro" && "Full access to all features"}
              {newRole === "beginner" && "Limited features, can top-up wallet and upgrade to Pro"}
              {newRole === "free" && "Signup credits only, cannot top-up wallet"}
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRoleDialog(false)}>
              Cancel
            </Button>
            <Button onClick={updateUserRole} disabled={updating}>
              {updating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Updating...
                </>
              ) : (
                "Update Role"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* User Profile Dialog */}
      <Dialog open={showProfileDialog} onOpenChange={setShowProfileDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>User Profile</DialogTitle>
            <DialogDescription>Detailed user information and activity</DialogDescription>
          </DialogHeader>
          {profileLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
            </div>
          ) : profileUser && (
            <div className="space-y-4">
              {/* User Header */}
              <div className="flex items-center gap-3 pb-3 border-b">
                <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center text-lg font-bold">
                  {profileUser.name?.[0]?.toUpperCase() || "U"}
                </div>
                <div>
                  <p className="font-semibold text-base" data-testid="profile-dialog-name">{profileUser.name || "No name"}</p>
                  <p className="text-sm text-muted-foreground" data-testid="profile-dialog-email">{profileUser.email}</p>
                </div>
                <div className="ml-auto">{getRoleBadge(profileUser.role)}</div>
              </div>

              {/* Account Info */}
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-muted-foreground text-xs">Auth Provider</p>
                  <p className="font-medium">{profileUser.auth_provider === "google" ? "Google" : "Email"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Email Verified</p>
                  <p className={`font-medium ${profileUser.email_verified ? "text-green-600" : "text-amber-600"}`}>
                    {profileUser.email_verified ? "Verified" : "Pending"}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Joined</p>
                  <p className="font-medium">{profileUser.created_at ? new Date(profileUser.created_at).toLocaleDateString("en-IN", { year: "numeric", month: "short", day: "numeric" }) : "N/A"}</p>
                </div>
                <div>
                  <p className="text-muted-foreground text-xs">Credits Balance</p>
                  <p className="font-semibold text-lg" data-testid="profile-dialog-credits">{profileUser.credits_balance ?? 0}</p>
                </div>
              </div>

              {/* Activity Stats */}
              {profileUser.stats && (
                <div>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2">Activity</p>
                  <div className="grid grid-cols-3 gap-3">
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <FolderOpen className="w-4 h-4 mx-auto mb-1 text-blue-500" />
                      <p className="text-lg font-bold" data-testid="profile-dialog-projects">{profileUser.stats.projects}</p>
                      <p className="text-[11px] text-muted-foreground">Projects</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <Camera className="w-4 h-4 mx-auto mb-1 text-green-500" />
                      <p className="text-lg font-bold" data-testid="profile-dialog-photoshoots">{profileUser.stats.photoshoots}</p>
                      <p className="text-[11px] text-muted-foreground">Photoshoots</p>
                    </div>
                    <div className="bg-muted/50 rounded-lg p-3 text-center">
                      <Palette className="w-4 h-4 mx-auto mb-1 text-purple-500" />
                      <p className="text-lg font-bold" data-testid="profile-dialog-recreations">{profileUser.stats.recreations}</p>
                      <p className="text-[11px] text-muted-foreground">Recreations</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="pt-2 border-t flex gap-2">
                <Button
                  size="sm"
                  onClick={() => setShowAddCreditsDialog(true)}
                  data-testid="admin-add-credits-btn"
                >
                  <Plus className="w-3.5 h-3.5 mr-1" />
                  Add Credits
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setShowProfileDialog(false);
                    handleRoleChange(profileUser);
                  }}
                >
                  Change Role
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add Credits Dialog */}
      <Dialog open={showAddCreditsDialog} onOpenChange={setShowAddCreditsDialog}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Add Credits</DialogTitle>
            <DialogDescription>
              Add credits to {profileUser?.name || profileUser?.email}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <label className="text-sm font-medium mb-2 block">Credit Amount</label>
            <Input
              type="number"
              min="1"
              step="1"
              value={creditAmount}
              onChange={(e) => setCreditAmount(e.target.value)}
              placeholder="e.g., 10"
              data-testid="credit-amount-input"
            />
            <p className="text-xs text-muted-foreground mt-2">
              Current balance: {profileUser?.credits_balance ?? 0} credits
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddCreditsDialog(false)}>Cancel</Button>
            <Button onClick={addCredits} disabled={addingCredits} data-testid="confirm-add-credits-btn">
              {addingCredits ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Adding...</> : "Add Credits"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
